/// Customer-problem record. The polymorphic target is encoded as a tagged
/// union of `{ kind: 'firm' | 'lawyer', id: string }` matching the server
/// DTO shape.
export type CustomerProblemTarget =
  | { kind: 'firm'; id: string }
  | { kind: 'lawyer'; id: string };

export type CustomerProblemStage =
  | 'latent'
  | 'emerging'
  | 'exploring'
  | 'evaluating'
  | 'adopting'
  | 'resolved'
  | 'recurring';

export type VerificationStatus = 'unconfirmed' | 'confirmed' | 'denied';

export const CUSTOMER_PROBLEM_STAGE_LABELS: Record<CustomerProblemStage, string> = {
  latent: '潜在',
  emerging: '顕在化',
  exploring: '情報収集',
  evaluating: '検討中',
  adopting: '導入準備',
  resolved: '解決済み',
  recurring: '再発・未定着',
};

export const CUSTOMER_PROBLEM_STAGE_ORDER: CustomerProblemStage[] = [
  'latent',
  'emerging',
  'exploring',
  'evaluating',
  'adopting',
  'resolved',
  'recurring',
];

export const VERIFICATION_STATUS_LABELS: Record<VerificationStatus, string> = {
  unconfirmed: '未確認',
  confirmed: '確認済',
  denied: '否定',
};

export const VERIFICATION_STATUS_ORDER: VerificationStatus[] = [
  'unconfirmed',
  'confirmed',
  'denied',
];

export interface CustomerProblem {
  id: string;
  target: CustomerProblemTarget;
  problem_id: string;
  stage: CustomerProblemStage;
  importance: number;
  urgency: number;
  confidence: number;
  evidence_summary: string | null;
  evidence_links: unknown;
  recommended_product_id: string | null;
  customer_strategy_summary: string | null;
  verification_status: VerificationStatus;
  source: string;
  created_at: string;
  updated_at: string;
}

export interface PagedCustomerProblems {
  items: CustomerProblem[];
  total: number;
  page: number;
  page_size: number;
}

export interface CustomerProblemListQuery {
  target_kind?: 'firm' | 'lawyer';
  firm_id?: string;
  lawyer_id?: string;
  problem_id?: string;
  stage?: CustomerProblemStage;
  include_rejected?: boolean;
  sort_by?: 'stage' | 'importance' | 'urgency' | 'created_at' | 'updated_at';
  sort_dir?: 'asc' | 'desc';
  page?: number;
  page_size?: number;
}

export interface CustomerProblemCreateRequest {
  target: CustomerProblemTarget;
  problem_id: string;
  stage: CustomerProblemStage;
  importance: number;
  urgency: number;
  evidence_summary?: string | null;
  recommended_product_id?: string | null;
  customer_strategy_summary?: string | null;
}

export interface CustomerProblemPatchRequest {
  // `problem_id` is intentionally omitted: the server locks it. Switching
  // a customer-problem to a different master problem belongs on a
  // delete + create flow so the original observation history stays clean.
  stage?: CustomerProblemStage;
  importance?: number;
  urgency?: number;
  evidence_summary?: string | null;
  recommended_product_id?: string | null;
  customer_strategy_summary?: string | null;
  verification_status?: VerificationStatus | null;
}
